import { useState } from 'react';
import {
  apiAutomationActions,
  apiAutomationRun,
  apiAutomationRuns,
  apiListAutomations,
  type AutomationRun,
  type RunStatus,
} from '~/lib/api/automations';
import { classNames } from '~/utils/classNames';
import {
  BTN_GHOST,
  BTN_SECONDARY,
  CARD,
  LoadError,
  Pill,
  Section,
  Segmented,
  SkeletonCard,
  Spinner,
} from '~/components/workbench/tools/payments/ui';
import { useResource } from '~/components/workbench/tools/payments/use-resource';
import { formatDate } from '~/components/workbench/tools/marketing/marketing-model';
import { RUN_FILTERS, actionTitle, runStatusMeta } from './automations-model';

const PAGE_SIZE = 25;

/** Every run of the app's automations (or one automation's), newest first, with step results. */
export function RunLog({
  appId,
  automationId,
  onClearFilter,
}: {
  appId: string;
  automationId?: string;
  onClearFilter: () => void;
}) {
  const [status, setStatus] = useState<'' | RunStatus>('');
  const [page, setPage] = useState(1);
  const runs = useResource(
    () => apiAutomationRuns(appId, { automationId, status: status || undefined, page, limit: PAGE_SIZE }),
    `${appId}:${automationId ?? ''}:${status}:${page}`,
  );
  const automations = useResource(() => apiListAutomations(appId), `${appId}:names`);
  const actions = useResource(() => apiAutomationActions(appId), `${appId}:actions`);
  const nameOf = (id: string) => automations.data?.find((a) => a.id === id)?.name ?? 'Deleted automation';
  const filtered = automationId ? nameOf(automationId) : null;

  return (
    <Section
      title="Run log"
      icon="i-ph:clock-counter-clockwise"
      aside={
        <Segmented
          label="Run status"
          value={status}
          options={RUN_FILTERS}
          onChange={(s) => {
            setStatus(s);
            setPage(1);
          }}
        />
      }
    >
      {filtered && (
        <div className="flex items-center gap-2 text-[11px] text-muted">
          Showing runs of <span className="text-secondary">{filtered}</span>
          <button
            onClick={onClearFilter}
            className="text-secondary underline-offset-2 hover:text-primary hover:underline"
          >
            Show all
          </button>
        </div>
      )}

      {runs.loading && !runs.data ? (
        <SkeletonCard lines={3} />
      ) : runs.error ? (
        <LoadError message="Could not load runs." onRetry={() => void runs.reload()} />
      ) : !runs.data?.runs.length ? (
        <div className={classNames(CARD, 'space-y-1.5 px-4 py-6 text-center')}>
          <div className="i-ph:clock-counter-clockwise mx-auto text-xl text-faint" />
          <p className="text-[12px] leading-relaxed text-muted">
            {page > 1 ? 'No more runs.' : 'No runs yet. They show up here as your automations run.'}
          </p>
        </div>
      ) : (
        <div className={classNames(CARD, 'divide-y divide-hairline overflow-hidden')}>
          {runs.data.runs.map((r) => (
            <RunRowItem
              key={r.id}
              appId={appId}
              run={r}
              name={nameOf(r.automationId)}
              actionTitle={(id) => actionTitle(actions.data ?? [], id)}
            />
          ))}
        </div>
      )}

      {runs.data && (page > 1 || runs.data.hasMore) && (
        <div className="flex items-center justify-between">
          <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1} className={BTN_GHOST}>
            <div className="i-ph:caret-left text-[13px]" />
            Newer
          </button>
          <span className="text-[11px] text-muted">Page {page}</span>
          <button onClick={() => setPage((p) => p + 1)} disabled={!runs.data.hasMore} className={BTN_GHOST}>
            Older
            <div className="i-ph:caret-right text-[13px]" />
          </button>
        </div>
      )}
    </Section>
  );
}

function RunRowItem({
  appId,
  run,
  name,
  actionTitle: titleOf,
}: {
  appId: string;
  run: AutomationRun;
  name: string;
  actionTitle: (id: string) => string;
}) {
  const [open, setOpen] = useState(false);
  const meta = runStatusMeta(run);
  const detail = useResource(open ? () => apiAutomationRun(appId, run.id) : null, `${run.id}:${open}`);
  const shown = detail.data ?? run;

  return (
    <div className="px-4 py-3">
      <button
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-start justify-between gap-3 text-left"
        aria-expanded={open}
      >
        <div className="min-w-0">
          <div className="truncate text-[12px] font-medium text-primary">{name}</div>
          <div className="mt-0.5 text-[11px] text-muted">
            {run.event} · {formatDate(run.createdAt)}
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <Pill tone={meta.tone} label={meta.label} pulse={run.status === 'running'} />
          <div className={classNames(open ? 'i-ph:caret-up' : 'i-ph:caret-down', 'text-[12px] text-muted')} />
        </div>
      </button>

      {open && (
        <div className="mt-3 space-y-2">
          {detail.loading && !detail.data && <Spinner />}
          {shown.error && <p className="text-[11px] leading-relaxed text-warning">{shown.error}</p>}
          {shown.status === 'pending' && shown.nextAttemptAt && (
            <p className="text-[11px] text-muted">Next try: {formatDate(shown.nextAttemptAt)}</p>
          )}
          {shown.results.length === 0 ? (
            <p className="text-[11px] text-muted">No steps ran.</p>
          ) : (
            <ol className="space-y-1.5">
              {shown.results.map((r, i) =>
                r ? (
                  <li key={i} className="flex items-start gap-2 rounded-lg border border-hairline bg-sunken px-3 py-2">
                    <div
                      className={classNames(
                        r.ok ? 'i-ph:check-circle text-success' : 'i-ph:x-circle text-danger',
                        'mt-px shrink-0 text-[13px]',
                      )}
                    />
                    <div className="min-w-0 text-[11px] leading-relaxed">
                      <div className="text-secondary">
                        {i + 1}. {titleOf(r.action)}
                        {r.continued && <span className="text-muted"> (kept going)</span>}
                      </div>
                      {r.message && <div className="break-words text-muted">{r.message}</div>}
                      {r.output && Object.keys(r.output).length > 0 && (
                        <code className="block break-all font-geist-mono text-[10px] text-faint">
                          {JSON.stringify(r.output)}
                        </code>
                      )}
                    </div>
                  </li>
                ) : null,
              )}
            </ol>
          )}
          {open && detail.data && (
            <details className="text-[11px] text-muted">
              <summary className="cursor-pointer select-none text-secondary">Event data</summary>
              <pre className="mt-1.5 max-h-48 overflow-auto rounded-lg border border-hairline bg-sunken p-2.5 font-geist-mono text-[10px] text-secondary">
                {JSON.stringify(detail.data.data, null, 2)}
              </pre>
            </details>
          )}
          {Boolean(detail.error) && (
            <button onClick={() => void detail.reload()} className={BTN_SECONDARY}>
              Retry
            </button>
          )}
        </div>
      )}
    </div>
  );
}
